import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Row, Col, Typography, Tabs, Tooltip, theme } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Permission } from '@aid/shared';
import { PageHeader } from '../../components/PageHeader';
import { useDashboard } from './api/useDashboard';
import { KpiCard } from './components/KpiCard';
import { SerieMensualChart } from './components/SerieMensualChart';
import { MovimientoClientesChart } from './components/MovimientoClientesChart';
import { ServiciosByEstadoChart } from './components/ServiciosByEstadoChart';
import { ClientesMap } from './components/ClientesMap';
import { TopDeudoresCard } from './components/TopDeudoresCard';
import { PlanVsRealCard } from './components/PlanVsRealCard';
import { DashboardSkeleton } from './components/DashboardSkeleton';
import { DashboardCard } from '../../components/DashboardCard';
import { useHasAnyPermission, useHasCobradorPaymentScope } from '../../auth/useHasPermission';
import { formatCurrency } from '../../lib/format';
import { useT } from '../../locales';
import {
  BUSINESS_TIME_ZONE,
  DATE_LOCALE,
  SECTION_GAP,
  capitalize,
  formatBusinessDate,
  formatBusinessMonth,
  formatDateLong,
} from './constants';
import { buildKpiCaptions } from './dashboard-captions';
import { RutaCobros } from './components/RutaCobros';
import { FacturacionPorDia } from './components/FacturacionPorDia';

const { Text } = Typography;

function isMissingAmount(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

export function DashboardPage() {
  const t = useT();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, isError, refetch, isRefetching } = useDashboard();
  const canGenerarCobros = useHasAnyPermission(Permission.COBROS_GENERAR);
  const isCobradorPaymentScope = useHasCobradorPaymentScope();
  const { token } = theme.useToken();

  if (isLoading) {
    return (
      <>
        <PageHeader title={t('dashboard.title')} />
        <DashboardSkeleton />
      </>
    );
  }

  if (isError || !data) {
    return (
      <>
        <PageHeader title={t('dashboard.title')} />
        <DashboardCard bodyStyle={{ padding: '20px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 14 }}>{t('dashboard.error')}</span>
            <Button type="primary" onClick={() => refetch()}>
              {t('dashboard.retry')}
            </Button>
          </div>
        </DashboardCard>
      </>
    );
  }

  const today = new Date();
  const dateLabel = formatDateLong(today);
  const subtitle = `${t('dashboard.subtitle')} · ${dateLabel}`;

  const { cobros, pagos } = data;
  const businessCutoff = cobros.currentMonth.fechaFin || pagos.currentMonth.fechaFin;
  const monthFormatter = new Intl.DateTimeFormat(DATE_LOCALE, {
    month: 'long',
    timeZone: BUSINESS_TIME_ZONE,
  });
  const monthName = businessCutoff
    ? formatBusinessMonth(businessCutoff)
    : monthFormatter.format(today);
  const prevMonthName = businessCutoff
    ? formatBusinessMonth(businessCutoff, -1)
    : monthFormatter.format(new Date(today.getFullYear(), today.getMonth() - 1, 1));
  const cutoffDateLabel = businessCutoff
    ? formatBusinessDate(businessCutoff)
    : t('common.notAvailable');
  const captions = buildKpiCaptions(t, data, monthName, prevMonthName);

  const moraActivos = Number(data.mora.activos.total);
  const moraConBaja = Number(data.mora.conBaja.total);
  const moraDeudaBaja = moraConBaja - moraActivos;
  const moraDeudaBajaClientes = data.mora.conBaja.clientes - data.mora.activos.clientes;

  // billing-domain-rules (Phase 8.4): saldo a favor aggregate, surfaced as a
  // distinct KPI card. NEVER netted with mora.activos.total — the two values
  // report independent state.
  //
  // The source is the credit ledger (available lots = the money the app will
  // consume on future invoices), served as `data.saldoAFavor`. It must NOT be
  // derived from `saldosTop`: that list feeds the "Deudores" tab and only
  // carries clients with open debt, so reducing it reported the credit of
  // people who owe money (production: Q0.00 while the ledger held Q1,861.24)
  // and never the credit of everyone else.
  // Defensive read: during a staggered deploy an older API may not send this
  // field yet. The card then shows 0 / "sin crédito" instead of taking the
  // whole dashboard down with it.
  const saldoAFavorTotal = Number(data.saldoAFavor?.total ?? 0);
  const saldoAFavorClientes = data.saldoAFavor?.clientes ?? 0;

  const clientesInactivos = data.clientesTotal - data.clientesActivos;

  // URL-driven tab state: ?tab=<key> persists the active tab across reloads.
  // Absent or unknown keys (incl. scope-hidden ones) fall back to 'resumen'.
  // 'ruta-cobros' is hidden for the active cobrador payment scope.
  const availableTabKeys = new Set(
    isCobradorPaymentScope
      ? ['resumen', 'mapa', 'cartera', 'facturacion-por-dia']
      : ['resumen', 'mapa', 'cartera', 'facturacion-por-dia', 'ruta-cobros'],
  );
  const tabParam = searchParams.get('tab');
  const activeTabKey = tabParam !== null && availableTabKeys.has(tabParam) ? tabParam : 'resumen';

  const handleTabChange = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next);
  };

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        subtitle={subtitle}
        showDivider={false}
        actions={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isRefetching}>
            {t('dashboard.refresh')}
          </Button>
        }
      />

      {captions.cobrosMesSinDatos && (
        <Alert
          type="warning"
          showIcon
          title={t('dashboard.cobrosSinGenerar', { mes: capitalize(monthName) })}
          style={{ marginBottom: SECTION_GAP }}
          action={
            canGenerarCobros ? (
              <Button size="small" type="primary" onClick={() => navigate('/facturas/generar')}>
                {t('dashboard.generarCobros')}
              </Button>
            ) : undefined
          }
        />
      )}

      <Tabs
        activeKey={activeTabKey}
        onChange={handleTabChange}
        destroyOnHidden
        items={[
          {
            key: 'resumen',
            label: t('dashboard.tabs.resumen'),
            children: (
              <>
                <Row
                  data-testid="dashboard-account-kpis"
                  gutter={[SECTION_GAP, SECTION_GAP]}
                  align="stretch"
                >
                  <Col xs={24} sm={12} lg={6}>
                    <KpiCard
                      label={t('dashboard.clientesActivos')}
                      value={String(data.clientesActivos)}
                      formatValue={false}
                      stack={[
                        { value: data.clientesActivos, color: token.colorInfo },
                        { value: clientesInactivos, color: token.colorTextTertiary },
                      ]}
                      caption={t('dashboard.clientesInactivosTotal', {
                        inactivos: clientesInactivos.toLocaleString(DATE_LOCALE),
                        total: data.clientesTotal.toLocaleString(DATE_LOCALE),
                      })}
                      accent="clientes"
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <KpiCard
                      label={t('dashboard.carteraMora')}
                      value={data.mora.activos.total}
                      accent="mora"
                      stack={[
                        { value: moraActivos, color: token.colorError },
                        { value: moraDeudaBaja, color: token.colorTextTertiary },
                      ]}
                      caption={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
                            {t('dashboard.deudoresCount', { count: data.mora.activos.clientes })}
                          </Text>
                          <Tooltip title={t('dashboard.moraDeudaBajaTooltip')} placement="top">
                            <Text
                              type="secondary"
                              style={{
                                fontSize: 11,
                                lineHeight: 1.3,
                                textDecoration: 'underline dotted',
                                cursor: 'help',
                              }}
                            >
                              {t('dashboard.moraDeudaBaja', {
                                monto: formatCurrency(moraDeudaBaja),
                                count: moraDeudaBajaClientes,
                              })}
                            </Text>
                          </Tooltip>
                        </div>
                      }
                    />
                  </Col>
                  {/* billing-domain-rules (Phase 8.4): saldo a favor as a
                      distinct, non-negative card. Never netted with mora. */}
                  <Col xs={24} sm={12} lg={6}>
                    <KpiCard
                      label={t('dashboard.saldoAFavorTotal')}
                      value={saldoAFavorTotal.toFixed(2)}
                      accent="pagos"
                      caption={
                        saldoAFavorClientes > 0
                          ? t('dashboard.saldoAFavorClientes', { count: saldoAFavorClientes })
                          : t('dashboard.saldoAFavorSinSaldo')
                      }
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <MovimientoClientesChart movimientoClientes={data.movimientoClientes} />
                  </Col>
                </Row>

                <section
                  data-testid="dashboard-operational-summary"
                  aria-labelledby="dashboard-operational-summary-title"
                  style={{ marginTop: SECTION_GAP }}
                >
                  <Text
                    id="dashboard-operational-summary-title"
                    strong
                    style={{ fontSize: 13, color: token.colorTextSecondary }}
                  >
                    {t('dashboard.operationalSummaryTitle')}
                  </Text>
                  <Row
                    gutter={[SECTION_GAP, SECTION_GAP]}
                    style={{ marginTop: 12 }}
                    align="stretch"
                  >
                    <Col xs={12} sm={12} lg={6}>
                      <KpiCard
                        compact
                        label={`${t('dashboard.cobrosHoy')} · ${monthName}`}
                        value={cobros.currentDay.total}
                        empty={isMissingAmount(cobros.currentDay.total)}
                        caption={captions.cobrosHoy}
                        accent="cobros"
                      />
                    </Col>
                    <Col xs={12} sm={12} lg={6}>
                      <KpiCard
                        compact
                        label={`${t('dashboard.cobrosMes')} · ${monthName}`}
                        value={cobros.currentMonth.total}
                        empty={
                          captions.cobrosMesSinDatos || isMissingAmount(cobros.currentMonth.total)
                        }
                        caption={
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
                              {t('dashboard.mesEnCursoCorte', { fecha: cutoffDateLabel })}
                            </Text>
                            {captions.cobrosMes && (
                              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
                                {captions.cobrosMes}
                              </Text>
                            )}
                          </div>
                        }
                        accent="cobros"
                      />
                    </Col>
                    <Col xs={12} sm={12} lg={6}>
                      <KpiCard
                        compact
                        label={`${t('dashboard.pagosHoy')} · ${monthName}`}
                        value={pagos.currentDay.total}
                        empty={isMissingAmount(pagos.currentDay.total)}
                        caption={captions.pagosHoy}
                        accent="pagos"
                      />
                    </Col>
                    <Col xs={12} sm={12} lg={6}>
                      <KpiCard
                        compact
                        label={`${t('dashboard.pagosMes')} · ${monthName}`}
                        value={pagos.currentMonth.total}
                        empty={
                          captions.pagosMesSinDatos || isMissingAmount(pagos.currentMonth.total)
                        }
                        caption={
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
                              {t('dashboard.mesEnCursoCorte', { fecha: cutoffDateLabel })}
                            </Text>
                            {captions.pagosMes && (
                              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
                                {captions.pagosMes}
                              </Text>
                            )}
                          </div>
                        }
                        accent="pagos"
                      />
                    </Col>
                  </Row>
                </section>

                <div
                  data-testid="dashboard-financial-performance"
                  style={{ marginTop: SECTION_GAP }}
                >
                  <SerieMensualChart serie={data.serieMensual} fechaCorte={cutoffDateLabel} />
                </div>

                <Row
                  data-testid="dashboard-operation-composition"
                  gutter={[SECTION_GAP, SECTION_GAP]}
                  style={{ marginTop: SECTION_GAP }}
                  align="stretch"
                >
                  <Col xs={24} lg={16}>
                    <PlanVsRealCard />
                  </Col>
                  <Col xs={24} lg={8}>
                    <ServiciosByEstadoChart servicios={data.servicios} />
                  </Col>
                </Row>
              </>
            ),
          },
          {
            key: 'mapa',
            label: t('dashboard.tabs.mapa'),
            children: <ClientesMap points={data.mapPoints} />,
          },
          {
            key: 'cartera',
            label: t('dashboard.tabs.cartera'),
            children: <TopDeudoresCard data={data.saldosTop} />,
          },
          {
            key: 'facturacion-por-dia',
            label: t('dashboard.tabs.facturacionPorDia'),
            children: <FacturacionPorDia />,
          },
          ...(!isCobradorPaymentScope
            ? [
                {
                  key: 'ruta-cobros',
                  label: t('dashboard.tabs.rutaCobros'),
                  children: <RutaCobros />,
                },
              ]
            : []),
        ]}
      />
    </>
  );
}
